/** Server-mediated Google Classroom teaching-loop contract.
 *
 * The browser holds a PKCE verifier only long enough to complete an OAuth
 * popup. It never receives a Google access token and never posts provider
 * objects back to IMAM.
 */
import { api } from "@/lib/api/client";
import { decodeClassroomExactRecord } from "@/lib/api/courseHomeV6Decoder";
import type {
  ClassroomObservationState,
  ClassroomRecoveryV6,
  ClassroomSubmissionV6,
  LearningClassroomCourseworkV6,
  TeachingClassroomCourseworkV6,
} from "@/lib/api/courseHome";

export type ClassroomAvailability = "disabled" | "not_connected" | "reconnect_required" | "disconnect_pending" | "not_mapped" | "ready" | "empty" | "stale" | "unavailable" | "access_denied";
export type ClassroomAction = "connect" | "discover_courses" | "map_course" | "discover_roster" | "map_learner" | "refresh" | "coursework_create" | "submission_turn_in" | "submission_reclaim" | "grade_patch" | "submission_return" | "result_proposal";

/** One of the connected teacher's active Classroom courses, as Google lists it. */
export interface ClassroomDiscoveredCourse {
  id: string;
  name: string;
  section?: string | null;
  alternate_link?: string | null;
}

/** A Classroom roster member. The Google person id is the key; name and email are evidence. */
export interface ClassroomDiscoveredMember {
  user_id: string;
  full_name?: string | null;
  email?: string | null;
}

export interface ClassroomDiscoveredRoster {
  provider_course_id: string;
  unpaired_students: ClassroomDiscoveredMember[];
  unpaired_enrollments: Array<{ enrollment_id: string; display_name: string; status?: string }>;
  co_teachers_without_assignment: Array<{ full_name: string }>;
}

export interface ClassroomMaterial {
  driveFile?: { id: string; alternateLink?: string; title?: string };
  link?: { url: string; title?: string };
}

export interface ClassroomSubmission {
  id: string;
  institutional_id: string;
  provider_submission_id?: string;
  state: string;
  assignedGrade?: number;
  draftGrade?: number;
  assignmentSubmission?: { attachments?: ClassroomMaterial[] };
  alternateLink?: string;
  copy_url?: string | null;
  copy_title?: string | null;
  provider_url?: string | null;
  history?: Array<{ state: string; at: string }>;
  copy_state?: "not_observed" | "ready" | "delayed_copy" | "unavailable";
  last_successful_at: string | null;
  unavailable_at: string | null;
}

export interface ClassroomCoursework {
  id: string;
  institutional_id: string;
  provider_coursework_id: string;
  provenance?: "app_created" | "provider_discovered";
  provider_write_eligible?: boolean;
  title: string;
  description?: string;
  due_at?: string | null;
  state: string;
  alternateLink?: string;
  maxPoints?: number;
  topicId?: string;
  materials?: ClassroomMaterial[];
  last_successful_at: string | null;
  unavailable_at: string | null;
  posted_read_at?: string | null;
  submissions?: ClassroomSubmission[];
  my_submission?: ClassroomSubmission | null;
}

export interface ClassroomUnresolvedOperation {
  id: string;
  kind: ClassroomAction;
  state: "pending" | "unknown";
  intent?: Record<string, unknown>;
  correlation_marker: string | null;
  provider_resources: Record<string, string> | null;
  failure_classification: string | null;
  created_at: string;
  updated_at: string;
  reconciliation_action?: ClassroomAction;
  requires_oauth?: boolean;
}

/**
 * A teacher pairs a learner from the Classroom roster (`teacher_paired`), or a
 * registrar's Classroom intake asserts the pairing (`registrar_asserted`); the
 * pairing is `learner_verified` once the learner's own verified Google account
 * matches it. A different account is recorded as a mismatch and changes nothing.
 */
export type ClassroomPairingVerification = "teacher_paired" | "registrar_asserted" | "learner_verified";

/**
 * How a `learner_verified` pairing was verified: the learner confirmed an
 * existing pairing, paired themselves from their own Classroom enrollment, or a
 * registrar linked a Google id IMAM had already verified for them.
 */
export type ClassroomPairingMethod = "learner_confirmed" | "learner_self_paired" | "verified_identity";

export interface ClassroomLearnerPairing {
  verification_state: ClassroomPairingVerification;
  verification_method?: ClassroomPairingMethod | null;
  mismatch_observed_at: string | null;
}

export interface ClassroomLearnerMapping extends ClassroomLearnerPairing {
  enrollment_id: string;
  provider_user_id: string;
  verified_at: string | null;
  learner_verified_at?: string | null;
}

export type ClassroomPairingState = "awaiting_learner" | "confirmed" | "different_account";

/** The one register state a pairing shows; a recorded mismatch outranks waiting. */
export function classroomPairingState(pairing: ClassroomLearnerPairing): ClassroomPairingState {
  if (pairing.verification_state === "learner_verified") return "confirmed";
  return pairing.mismatch_observed_at ? "different_account" : "awaiting_learner";
}

export interface ClassroomCourseProjection {
  availability: ClassroomAvailability;
  last_successful_at: string | null;
  unavailable_at: string | null;
  connection: { id: string; account_label?: string; provider_user_id?: string; verified_at?: string; credential_mode: "transactional_user_oauth" | "connected_user_oauth"; grant_state: "transactional" | "pending_custody" | "active" | "reconnect_required" | "disconnect_pending" | "revoked"; granted_scopes: string[]; connected_execution_available: boolean } | null;
  course_mapping: { id: string; provider_course_id: string; name?: string; section?: string | null; alternate_link?: string; verified_at?: string } | null;
  learner_mappings: ClassroomLearnerMapping[];
  /** The caller's own pairing, on the learner perspective only. It names no Google id. */
  learner_pairing?: ClassroomLearnerPairing | null;
  unresolved_operations: ClassroomUnresolvedOperation[];
  topics: Array<{ id: string; name: string }>;
  coursework: ClassroomCoursework[];
}

export type ClassroomActionPayload =
  | Record<string, never>
  | { provider_course_id: string }
  | { enrollment_id: string; expected_provider_user_id: string }
  | { idempotency_key: string; title: string; instructions?: string; max_points?: number; due_date?: { year: number; month: number; day: number }; due_time?: { hours: number; minutes: number; seconds?: number }; topic_id?: string; template_material_id: string; learner_enrollment_ids: string[] }
  | { idempotency_key: string; coursework_id: string; submission_id: string }
  | { idempotency_key: string; coursework_id: string; submission_id: string; draft_grade?: number; assigned_grade?: number }
  | { coursework_id: string; submission_id: string; result_letter: string; result_score?: number };

export interface ClassroomAuthorization {
  authorization_url: string;
  state: string;
  action: ClassroomAction;
}

/** What a learner with no pairing learned when they connected their own Google account. */
export type ClassroomSelfPairingOutcome = "paired" | "not_a_student" | "account_paired_elsewhere";

export interface ClassroomCompletion {
  action: ClassroomAction;
  learner_self_pairing?: ClassroomSelfPairingOutcome;
  courses?: ClassroomDiscoveredCourse[];
  roster?: ClassroomDiscoveredRoster;
  projection?: ClassroomCourseProjection;
  connection?: ClassroomCourseProjection["connection"];
  course_mapping?: ClassroomCourseProjection["course_mapping"];
  learner_mapping?: ClassroomCourseProjection["learner_mappings"][number];
  coursework?: ClassroomCoursework;
  submission?: ClassroomSubmission;
  result_proposal?: { id: string; result_letter: string; result_score: number | null };
}

export interface ClassroomExactRecord {
  observation: {
    state: ClassroomObservationState;
    last_successful_at: string | null;
    unavailable_at: string | null;
  };
  recovery: ClassroomRecoveryV6 | null;
  coursework: TeachingClassroomCourseworkV6 | LearningClassroomCourseworkV6;
  submission: ClassroomSubmissionV6 | null;
}

export function classroomCourseQueryKey(
  tenantId: string,
  userId: string,
  courseId: string,
): readonly unknown[] {
  return ["classroom-course", tenantId, userId, courseId];
}

export function classroomExactQueryKey(
  tenantId: string,
  userId: string,
  courseId: string,
  courseworkId: string,
  submissionId: string | null,
): readonly unknown[] {
  return ["classroom-exact", tenantId, userId, courseId, courseworkId, submissionId];
}

export const classroomApi = {
  getCourse: (courseId: string) => api.get<ClassroomCourseProjection>(`/courses/${courseId}/classroom`),
  getCoursework: async (courseId: string, courseworkId: string) =>
    decodeClassroomExactRecord(await api.get<unknown>(
      `/courses/${encodeURIComponent(courseId)}/classroom/coursework/${encodeURIComponent(courseworkId)}`,
    )),
  getSubmission: async (courseId: string, courseworkId: string, submissionId: string) =>
    {
      const exact = decodeClassroomExactRecord(await api.get<unknown>(
        `/courses/${encodeURIComponent(courseId)}/classroom/coursework/${encodeURIComponent(courseworkId)}/submissions/${encodeURIComponent(submissionId)}`,
      ));
      if (exact.submission === null) {
        throw new Error("Classroom exact record response is invalid.");
      }
      return exact;
    },
  disconnect: (courseId: string) =>
    api.post<{ disconnected: boolean; connections_revoked: number; course_mappings_revoked: number; learner_mappings_revoked: number }>(`/courses/${courseId}/classroom/disconnect`, {}),
};
